// ─── Template : BARRE recherche + filtre + tri + réinitialiser (Core) ──
// UN SEUL gabarit pour toute liste avec recherche, réutilisable tel quel :
//
//   <BarreRecherche valeur={q} onChange={setQ} placeholder="Rechercher…"
//     nbFiltres={n} onFiltres={ouvrirMesFiltres}
//     triActif={triNonDefaut} onTri={basculerOuOuvrirMonTri}
//     onReinitialiser={() => { setQ(''); remettreFiltresAZero(); }} />
//
// Chaque bouton est OPTIONNEL — absent sans son `onXxx` correspondant :
//   🔽 filtre (gauche) · champ + ✕ pour vider le texte · ↕ tri (droite)
//   · ↺ réinitialiser (n'apparaît QUE si un filtre/une recherche/un tri
//     non-défaut est actif — sinon rien à réinitialiser).
// `onFiltres` et `onTri` sont de simples callbacks : à l'écran d'ouvrir ce
// qu'il veut derrière (CalqueChoix, FeuilleFiltres, un tri à un ou plusieurs
// critères…) — le gabarit ignore volontairement la FORME des filtres, pour
// rester réutilisable par n'importe quel écran de liste.
//
// Fusionne deux gabarits qui se chevauchaient (19/08, Cédric) :
//   • BarreRecherche (My Finance, 18/08 → promue Core) : filtre + tri.
//   • BarreFiltres (Core, MoP Admin `EcranComptes`) : filtre (groupes
//     multi-sélection) + réinitialiser combiné, mais pas de tri.
// Le modèle « groupes/valeurs » de BarreFiltres reste entièrement possible
// ICI : `nbFiltres` se calcule côté écran (ex.
// `groupes.filter(g => Array.isArray(valeurs[g.cle])).length`) et
// `onReinitialiser` vide filtres ET recherche comme le faisait son ↺.
import React, { useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function BarreRecherche({
  valeur, onChange, placeholder, nbFiltres = 0, onFiltres, triActif = false, onTri,
  onReinitialiser, style,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const aQuelqueChose = nbFiltres > 0 || !!valeur || triActif;

  return (
    <View style={[s.controles, style]}>
      {!!onFiltres && (
        <TouchableOpacity style={[s.btnIc, nbFiltres > 0 && s.btnIcOn]} activeOpacity={0.7}
          onPress={onFiltres} accessibilityLabel={t('commun.filtrer')}>
          <Text style={s.ic}>🔽</Text>
          {nbFiltres > 0 && (
            <View style={s.pastille}><Text style={s.pastilleTxt}>{nbFiltres}</Text></View>
          )}
        </TouchableOpacity>
      )}

      <View style={s.rechercheZone}>
        <TextInput style={s.recherche} value={valeur} onChangeText={onChange}
          placeholder={placeholder ?? t('commun.rechercher')} placeholderTextColor={UI.faint} />
        {!!valeur && (
          <TouchableOpacity onPress={() => onChange?.('')} hitSlop={HIT}>
            <Text style={{ color: UI.muted, fontSize: 15 }}>✕</Text>
          </TouchableOpacity>
        )}
      </View>

      {!!onTri && (
        <TouchableOpacity style={[s.btnIc, triActif && s.btnIcOn]} activeOpacity={0.7}
          onPress={onTri} accessibilityLabel={t('commun.trier')}>
          <Text style={s.ic}>↕</Text>
        </TouchableOpacity>
      )}

      {!!onReinitialiser && aQuelqueChose && (
        <TouchableOpacity onPress={onReinitialiser} hitSlop={HIT} accessibilityLabel={t('commun.reinitialiser')}>
          <Text style={s.reinit}>↺</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  controles: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  btnIc: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnIcOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ic: { fontSize: 17 },
  pastille: { position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, borderRadius: 999, paddingHorizontal: 4, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  pastilleTxt: { fontSize: 10.5, color: '#fff', fontWeight: '800' },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  reinit: { fontSize: 19, color: UI.accent, fontWeight: '700' },
});
